import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getKitsList, toggleKitStatus, getWarehouses } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';
import KitHubMatrix from '../components/KitHubMatrix';

const Kits = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [toggling, setToggling] = useState(null);
  // Desktop view: 'matrix' (kit × hub grid) or 'list' (one row per hub copy)
  const [view, setView] = useState(() => localStorage.getItem('kitsView') || 'matrix');
  const showMatrix = !isMobile && view === 'matrix';

  const changeView = (nextView) => {
    setView(nextView);
    localStorage.setItem('kitsView', nextView);
    if (nextView === 'matrix') setSelectedWarehouse('');
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin' && !userData.access?.includes('marketing')) {
      window.location.href = '/orders';
      return;
    }
    loadWarehouses();
  }, []);

  useEffect(() => {
    loadKits();
  }, [selectedWarehouse]);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        setWarehouses(response.data);
        // Default stays as '' (All Warehouses) — no auto-selection
      }
    } catch (error) {
      toast.error('Failed to load warehouses');
    }
  };

  const getWarehouseName = (whId) => {
    if (!whId) return 'All Hubs';
    const wh = warehouses.find((w) => String(w.id) === String(whId));
    if (!wh) return `Warehouse #${whId}`;
    const isVirtual = wh.hub_type === 'VIRTUAL' || wh.hubType === 'VIRTUAL';
    return `${wh.name} (${wh.city})${isVirtual ? ' (Virtual Hub)' : ''}`;
  };

  const loadKits = async () => {
    setLoading(true);
    try {
      const response = await getKitsList(selectedWarehouse);
      if (response && response.data && response.data.kits) {
        setKits(response.data.kits);
      } else {
        setKits([]);
      }
    } catch (error) {
      toast.error('Failed to load kits');
      setKits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (kitId) => {
    setToggling(kitId);
    try {
      await toggleKitStatus(kitId);
      toast.success('Kit status updated');
      loadKits();
    } catch (error) {
      toast.error('Failed to toggle kit status');
    } finally {
      setToggling(null);
    }
  };

  const renderCategories = (kit) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {(kit.categories || []).map((cat, i) => (
        <span key={i} style={{
          background: cat.is_mandatory ? '#e3f2fd' : '#fff3e0',
          color: cat.is_mandatory ? '#1565c0' : '#e65100',
          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500,
        }}>
          {cat.category_name} (×{cat.min_qty}{cat.max_qty !== cat.min_qty ? `-${cat.max_qty}` : ''})
        </span>
      ))}
    </div>
  );

  const renderStatus = (kit) => (
    <span style={{
      background: kit.active ? '#d1e7dd' : '#f8d7da',
      color: kit.active ? '#0f5132' : '#842029',
      padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
    }}>
      {kit.active ? 'Active' : 'Inactive'}
    </span>
  );

  const renderActions = (kit) => (
    <div style={{ display: 'flex', gap: isMobile ? '8px' : '4px', justifyContent: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap' }}>
      <button onClick={() => navigate(`/kits/view/${kit.id}`)} className="btn btn-primary" style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 14px' : '4px 8px' }}>
        View
      </button>
      <button
        onClick={() => {
          const isFromAll = selectedWarehouse === '' || selectedWarehouse === 'all';
          const query = isFromAll
            ? `?fromWarehouse=all&warehouseId=${kit.warehouse_id || 'all'}`
            : `?warehouseId=${selectedWarehouse || kit.warehouse_id || '1'}`;
          navigate(`/kits/edit/${kit.id}${query}`);
        }}
        className="btn btn-warning"
        style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 14px' : '4px 8px' }}
      >
        Edit
      </button>
      <button
        onClick={() => handleToggle(kit.id)}
        disabled={toggling === kit.id}
        className="btn"
        style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 14px' : '4px 8px', background: kit.active ? '#dc3545' : '#198754', color: 'white' }}
      >
        {toggling === kit.id ? '...' : kit.active ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  );

  return (
    <div className="container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Custom Kits</h1>
          <p className="page-subtitle">Manage solar kits built from product categories</p>
        </div>
      </div>

      {/* Warehouse Filter + Add Kit */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {!isMobile && (
              <div style={{ display: 'inline-flex', border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden' }}>
                {[['matrix', 'Kit × Hub'], ['list', 'List']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => changeView(value)}
                    style={{ border: 'none', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: view === value ? '#0d6efd' : 'white', color: view === value ? 'white' : '#495057' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {!showMatrix && (<>
            <label style={{ fontWeight: 600, fontSize: '13px' }}>Select Warehouse</label>
            <select
              className="form-control"
              style={{ maxWidth: '280px' }}
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
            >
              <option value="">All Warehouses</option>
              {warehouses.map((wh) => {
                const isVirtual = wh.hub_type === 'VIRTUAL' || wh.hubType === 'VIRTUAL';
                return (
                  <option key={wh.id} value={wh.id}>
                    {wh.name} - {wh.city}{isVirtual ? ' (Virtual Hub)' : ''}
                  </option>
                );
              })}
            </select>
            </>)}
          </div>
          <Link to={`/kits/create?warehouseId=${selectedWarehouse || 'all'}`} className="btn btn-success" style={{ fontSize: '14px', padding: '10px 20px' }}>
            + Add Kit {!selectedWarehouse && <span style={{ fontSize: '11px', opacity: 0.8 }}>(All Hubs)</span>}
          </Link>
        </div>
      </div>

      {/* Kit List Table */}
      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Loading kits...</div>
        ) : kits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p style={{ marginBottom: '12px' }}>
              {selectedWarehouse ? 'No kits found for this warehouse.' : 'No custom kits found.'}
            </p>
            <Link to={`/kits/create?warehouseId=${selectedWarehouse || '1'}`} className="btn btn-primary">
              Create your first kit
            </Link>
          </div>
        ) : showMatrix ? (
          <KitHubMatrix kits={kits} warehouses={warehouses} onReload={loadKits} />
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {kits.map((kit) => (
              <div key={kit.id} style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  {kit.image && kit.image.startsWith('http') && <img src={kit.image} alt="" style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px', flex: '0 0 auto' }} />}
                  <div style={{ fontWeight: 600, fontSize: '15px', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{kit.name}</div>
                  {renderStatus(kit)}
                </div>
                {!selectedWarehouse && (
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', background: '#f0f4f8', color: '#1565c0', padding: '3px 8px', borderRadius: '4px', fontWeight: 500 }}>
                      {getWarehouseName(kit.warehouse_id)}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', color: '#495057', marginBottom: '8px' }}>
                  <span>Discount: <strong>{kit.discount_percent}%</strong></span>
                  <span>GST: <strong>{kit.gst_percent || '-'}%</strong></span>
                  <span>HSN: <strong>{kit.hsn_code || '-'}</strong></span>
                  <span>Min Optional: <strong>{kit.min_optional_count}</strong></span>
                </div>
                <div style={{ marginBottom: '10px' }}>{renderCategories(kit)}</div>
                {renderActions(kit)}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Kit Name</th>
                  {!selectedWarehouse && (
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Warehouse</th>
                  )}
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Discount %</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>GST %</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>HSN</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Categories</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Min Optional</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {kits.map((kit) => (
                  <tr key={kit.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {kit.image && kit.image.startsWith('http') && <img src={kit.image} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px' }} />}
                        {kit.name}
                      </div>
                    </td>
                    {!selectedWarehouse && (
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: '11px', background: '#f0f4f8', color: '#1565c0', padding: '3px 8px', borderRadius: '4px', fontWeight: 500 }}>
                          {getWarehouseName(kit.warehouse_id)}
                        </span>
                      </td>
                    )}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{kit.discount_percent}%</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{kit.gst_percent || '-'}%</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{kit.hsn_code || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {renderCategories(kit)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{kit.min_optional_count}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {renderStatus(kit)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {renderActions(kit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default Kits;
